import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, Linkedin, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTION_TYPES = [
  "Film",
  "OTT & Web Series",
  "Television",
  "Commercial",
  "Documentary",
  "Independent Production",
  "Other",
];

const EMPTY = { name: "", email: "", phone: "", company: "", production_type: "", message: "" };

const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-neutral-100 outline-none transition-colors duration-300 placeholder:text-neutral-600 focus:border-[#B79045]";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Enquiry received. We will be in touch shortly.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please email us directly at suyog@veritas.co.in.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
            <span className="h-px w-10 bg-[#B79045]" /> Your Production. Our Financial Control.
          </p>
          <h2 className="max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Let&rsquo;s Build a Stronger{" "}
            <em className="italic text-[#D4AF37]">Financial Framework</em> for Your
            Next Project.
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-400 md:text-base">
            Put financial control in place from Day One — not at the end of the
            production. For Commercial Management, Project Audit, Accounting and
            Production Finance requirements.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12 md:mt-24">
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                Contact
              </p>
              <h3 className="mt-4 font-display text-3xl font-medium text-neutral-100">
                Suyog Madhukar Pulekar
              </h3>

              <div className="mt-10 space-y-0 border-t border-white/10">
                <a
                  data-testid="contact-phone-link"
                  href="tel:+918850845977"
                  className="group flex items-center justify-between border-b border-white/10 py-5 transition-colors duration-300 hover:bg-[#0f0d09]"
                >
                  <span className="flex items-center gap-4">
                    <Phone size={16} className="text-[#B79045]" />
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Phone</span>
                      <span className="text-sm text-neutral-200">+91 88508 45977</span>
                    </span>
                  </span>
                  <ArrowUpRight size={16} className="text-neutral-600 transition-colors duration-300 group-hover:text-[#D4AF37]" />
                </a>
                <a
                  data-testid="contact-email-link"
                  href="mailto:suyog@veritas.co.in"
                  className="group flex items-center justify-between border-b border-white/10 py-5 transition-colors duration-300 hover:bg-[#0f0d09]"
                >
                  <span className="flex items-center gap-4">
                    <Mail size={16} className="text-[#B79045]" />
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Email</span>
                      <span className="text-sm text-neutral-200">suyog@veritas.co.in</span>
                    </span>
                  </span>
                  <ArrowUpRight size={16} className="text-neutral-600 transition-colors duration-300 group-hover:text-[#D4AF37]" />
                </a>
                <a
                  data-testid="contact-linkedin-link"
                  href="https://www.linkedin.com/in/suyog-pulekar-753bb25a"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between border-b border-white/10 py-5 transition-colors duration-300 hover:bg-[#0f0d09]"
                >
                  <span className="flex items-center gap-4">
                    <Linkedin size={16} className="text-[#B79045]" />
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">LinkedIn</span>
                      <span className="text-sm text-neutral-200">Suyog Madhukar Pulekar</span>
                    </span>
                  </span>
                  <ArrowUpRight size={16} className="text-neutral-600 transition-colors duration-300 group-hover:text-[#D4AF37]" />
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              {sent ? (
                <div
                  data-testid="enquiry-success-message"
                  className="flex h-full min-h-[320px] flex-col items-start justify-center border border-[#B79045]/40 bg-[#0f0d09] p-10"
                >
                  <CheckCircle2 size={32} className="text-[#D4AF37]" />
                  <h3 className="mt-6 font-display text-3xl font-medium text-neutral-100">
                    Enquiry received.
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-400">
                    Thank you for reaching out. Your details are with us — we will
                    get back to you shortly to discuss your production.
                  </p>
                  <button
                    data-testid="enquiry-send-another-button"
                    onClick={() => setSent(false)}
                    className="mt-8 border border-white/20 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form data-testid="enquiry-form" onSubmit={submit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Name *
                      </label>
                      <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Email *
                      </label>
                      <input id="enquiry-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@production.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Phone
                      </label>
                      <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Production House / Company
                      </label>
                      <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="enquiry-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Production Type
                    </label>
                    <select id="enquiry-type" data-testid="enquiry-type-select" value={form.production_type} onChange={set("production_type")} className={inputCls}>
                      <option value="">Select a production type</option>
                      {PRODUCTION_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      About Your Project *
                    </label>
                    <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your production — stage, scale, timelines..." className={`${inputCls} resize-none`} />
                  </div>
                  <button
                    data-testid="enquiry-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center gap-3 bg-[#B79045] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-60"
                  >
                    {sending ? (
                      <>Sending <Loader2 size={14} className="animate-spin" /></>
                    ) : (
                      <>Get in Touch <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
